import React from 'react';
import { FormikProps } from 'formik';
import { Label, Loading } from '@brighterion/br-ui';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { PCI_VALUE_INPUT, USE_ERROR_STYLE, LOADED, INPUT_CHANGE, INIT, TWELVE_TO_19_ANY, GET_TOKEN_ERROR } from 'Utils';
import { appInfoSelector, getUser } from 'Selectors';
import Iframe from 'Components/Iframe';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import { useAuthenticationStore } from 'Hooks/zustand/useAuthenticationStore';
import { useAccessTokenStore } from 'Hooks/zustand/useAccessTokenStore';

import styles from './styles.css';

type Props = {
    label: string;
    formikProps: FormikProps<any>;
    errorMsg: string;
};

const PCIInput = ({ label, formikProps, errorMsg }: Props) => {
    const { t } = useTranslation();
    const appInfo = useSelector(appInfoSelector);
    const { customerId } = useSelector(getUser);
    const iframeRef = React.useRef<HTMLIFrameElement>(null);
    const [loaded, setLoaded] = React.useState(false);
    const { addTechnicalErrorModal } = useReduxModal();
    const hasError = !!(formikProps.errors.isValid && formikProps.touched.isValid);

    React.useEffect(() => {
        if (errorMsg) {
            iframeRef.current?.contentWindow?.postMessage(
                {
                    type: USE_ERROR_STYLE
                },
                iframeRef.current.src
            );
            formikProps.setFieldError('isValid', errorMsg);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [errorMsg]);

    React.useEffect(() => {
        const listener = (e: MessageEvent) => {
            // do this to get origin
            const { origin } = new URL(appInfo!.pciComponentsURL);

            if (e.origin !== origin) return;
            if (e?.data) {
                const pData = e?.data;
                if (pData?.name === PCI_VALUE_INPUT) {
                    switch (pData?.type) {
                        case LOADED:
                            setLoaded(true);
                            break;
                        case INPUT_CHANGE:
                            formikProps.setFieldValue('isValid', pData.payload.isValid);
                            if (!formikProps.touched.isValid) {
                                formikProps.setFieldTouched('isValid');
                            }
                            break;
                        case GET_TOKEN_ERROR:
                            addTechnicalErrorModal(
                                t('pciInputError'),
                                t('Error Saving'),
                                async () => {
                                    /**
                                     * @note Refreshing the access token here is temp a solution.
                                     *
                                     * Since PAN search, field and uploader renders an iframe that
                                     * is uses the access token in the payload, refreshing
                                     * the access token inside the iframe is not working for now.
                                     * To make sure the user will have at least 10 minutes
                                     * before the access token expire, refreshing the tokens
                                     * happen when the user reloading the page.
                                     *
                                     * Impacted components:
                                     * - BcatsMaskedField
                                     * - PCISearch
                                     * - PCIFileUploader
                                     * - PCIInput
                                     */
                                    const { authService } = useAuthenticationStore.getState();
                                    await authService?.refreshAccessToken();
                                    useAccessTokenStore.setState({ accessToken: await authService?.getAccessToken() });

                                    window.location.reload();
                                },
                                {
                                    cancelText: t('Reload page')
                                }
                            );
                            break;
                        default:
                            break;
                    }
                }
            }
        };
        window.addEventListener('message', listener);
        return () => {
            window.removeEventListener('message', listener);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        if (loaded) {
            iframeRef.current?.contentWindow?.postMessage(
                {
                    type: INIT,
                    payload: {
                        jwt: axios.defaults.headers.common.Authorization,
                        tenantId: customerId,
                        validation: {
                            tests: [
                                {
                                    regex: TWELVE_TO_19_ANY
                                }
                            ]
                        }
                    }
                },
                iframeRef.current.src
            );
        }
    }, [loaded, customerId]);

    return (
        <div className={styles.formBlock}>
            <Label label={label} id='value' />
            <div className={styles.shortInput}>
                {!loaded && (
                    <div className={styles.loadingContainer}>
                        <Loading size='sm' />
                    </div>
                )}
                <Iframe
                    ref={iframeRef}
                    name={PCI_VALUE_INPUT}
                    id={PCI_VALUE_INPUT}
                    src={`${appInfo!.pciComponentsURL}/tokenize_single`}
                />
            </div>
            <div className={hasError ? 'InputBase__errorText' : 'InputBase__optionalText'}>
                {errorMsg ||
                    t('validation.between', {
                        minCharCount: 12,
                        maxCharCount: 19
                    })}
            </div>
        </div>
    );
};

export default React.memo(PCIInput);
